import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import Typography from '@material-ui/core/Typography';
import Box from '@material-ui/core/Box';
import Radio from '@material-ui/core/Radio';
import RadioGroup from '@material-ui/core/RadioGroup';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import FormControl from '@material-ui/core/FormControl';
import FormLabel from '@material-ui/core/FormLabel';
import CircularProgress from '@material-ui/core/CircularProgress';
import Paper from '@material-ui/core/Paper';
import makeStyles from '@material-ui/core/styles/makeStyles';

import coinSpendCodesClient from '../../../../clients/coin-spend-codes';

const NONE_VALUE = '';

const useStyles = makeStyles((theme) => ({
  root: {
    marginTop: theme.spacing(1),
  },
  title: {
    marginBottom: theme.spacing(1),
  },
  item: {
    padding: theme.spacing(1.5),
    marginBottom: theme.spacing(1),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
  },
  meta: {
    fontSize: '0.8125rem',
    color: theme.palette.text.secondary,
  },
  row: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(1),
  },
  muted: {
    opacity: 0.65,
  },
}));

const normId = (c) => String(c.documentId ?? c.id ?? '');

/**
 * Lists spend QR codes for the selected object; exactly one may be selected for this prize (radio).
 */
const PrizeSpendCodesField = ({
  objectDocumentId,
  selectedSpendCodeId,
  onSelectedSpendCodeChange,
  disabled,
}) => {
  const classes = useStyles();
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(false);
  const selectedRef = useRef(selectedSpendCodeId);
  const onChangeRef = useRef(onSelectedSpendCodeChange);

  useEffect(() => {
    selectedRef.current = selectedSpendCodeId;
  }, [selectedSpendCodeId]);

  useEffect(() => {
    onChangeRef.current = onSelectedSpendCodeChange;
  }, [onSelectedSpendCodeChange]);

  useEffect(() => {
    if (!objectDocumentId) {
      setCodes([]);
      onChangeRef.current(NONE_VALUE);
      return undefined;
    }

    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const list = await coinSpendCodesClient.findByObject(objectDocumentId);
        const arr = Array.isArray(list) ? list : [];
        if (cancelled) return;
        setCodes(arr);
        const valid = new Set(arr.map(normId).filter(Boolean));
        const current = String(selectedRef.current || '').trim();
        if (current && !valid.has(current)) {
          onChangeRef.current(NONE_VALUE);
        }
      } catch {
        if (!cancelled) {
          setCodes([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [objectDocumentId]);

  if (!objectDocumentId) {
    return null;
  }

  if (loading) {
    return (
      <Box display="flex" alignItems="center" className={classes.root} style={{ gap: 8 }}>
        <CircularProgress size={22} />
        <Typography variant="body2" color="textSecondary">
          Kraunami QR kodai…
        </Typography>
      </Box>
    );
  }

  const groupValue =
    codes.some((c) => normId(c) === String(selectedSpendCodeId || '').trim()) &&
    String(selectedSpendCodeId || '').trim()
      ? String(selectedSpendCodeId).trim()
      : NONE_VALUE;

  const handleRadioChange = (e) => {
    const v = e.target.value;
    onSelectedSpendCodeChange(v === NONE_VALUE ? NONE_VALUE : v);
  };

  return (
    <Box className={classes.root}>
      <FormControl component="fieldset" disabled={disabled} fullWidth>
        <FormLabel component="legend">
          <Typography variant="subtitle2" className={classes.title} component="span">
            QR kodas (monetų išleidimas) šiam prizui
          </Typography>
        </FormLabel>
        <Typography variant="caption" color="textSecondary" display="block" style={{ marginBottom: 8 }}>
          Pasirinkite vieną aktyvų QR kodą prizui arba „Nėra“. Globaliai išjungti kodai nepasirenkami.
        </Typography>
        {codes.length === 0 ? (
          <Paper variant="outlined" className={classes.item}>
            <Typography variant="body2" color="textSecondary">
              Šiam objektui dar nėra sugeneruotų QR kodų.
            </Typography>
          </Paper>
        ) : (
          <RadioGroup value={groupValue} onChange={handleRadioChange}>
            <FormControlLabel
              value={NONE_VALUE}
              control={<Radio color="primary" />}
              label="Nėra (joks QR nepriskirtas prizui)"
            />
            {codes.map((code) => {
              const id = normId(code);
              if (!id) return null;
              const globalOn = code.Enabled !== false;
              const label = (
                <Box className={!globalOn ? classes.muted : undefined}>
                  <Typography variant="body2" style={{ fontWeight: 600 }}>
                    {code.Name || id}
                  </Typography>
                  <Typography className={classes.meta}>
                    {code.Coins != null ? `${code.Coins} aukso monetų` : '—'}
                    {!globalOn ? ' · globaliai išjungta' : ''}
                  </Typography>
                </Box>
              );
              return (
                <Paper key={id} variant="outlined" className={classes.item}>
                  <Box className={classes.row}>
                    <FormControlLabel
                      value={id}
                      control={<Radio color="primary" disabled={!globalOn} />}
                      label={label}
                      disabled={!globalOn}
                    />
                  </Box>
                </Paper>
              );
            })}
          </RadioGroup>
        )}
      </FormControl>
    </Box>
  );
};

PrizeSpendCodesField.propTypes = {
  objectDocumentId: PropTypes.string,
  selectedSpendCodeId: PropTypes.string,
  onSelectedSpendCodeChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

PrizeSpendCodesField.defaultProps = {
  objectDocumentId: '',
  selectedSpendCodeId: '',
  disabled: false,
};

export default PrizeSpendCodesField;
